import { matchPath } from 'react-router-dom';
import { Actions, screenTypes } from 'Constants/permissions';

export function normalizePath(path) {
  if (!path) return '';
  return path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
}

export function parseUserFromStorage() {
  const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
  try {
    const rawPerms = localStorage.getItem('permissions');
    if (rawPerms) {
      const parsed = JSON.parse(rawPerms);
      if (Array.isArray(parsed) && parsed.length && !Array.isArray(userInfo.permission_codenames)) {
        userInfo.permission_codenames = parsed;
      }
    }
  } catch (e) {
    /* ignore */
  }
  let isServerMenu = false;
  try {
    const storedMenu = localStorage.getItem('menu');
    if (storedMenu) {
      const parsedMenu = JSON.parse(storedMenu);
      if (parsedMenu && typeof parsedMenu === 'object' && !Array.isArray(parsedMenu)) {
        isServerMenu = true;
      }
    }
  } catch (e) {
    /* ignore */
  }
  return { userInfo, isServerMenu };
}

export function computeIsSuperuser(userInfo) {
  if (userInfo?.is_superuser) return true;
  const roleName = (userInfo?.role || userInfo?.user_type || '').toString().toLowerCase();
  if (roleName === 'superadmin' || roleName === 'super_admin') return true;
  const groupNames = (userInfo?.groups || [])
    .map((groupItem) =>
      (typeof groupItem === 'string' ? groupItem : groupItem?.name || '').toString().toLowerCase()
    )
    .filter(Boolean);
  return groupNames.includes('superadmin') || groupNames.includes('super_admin');
}

export function computeUserPermissionCodes(userInfo) {
  const normalizeCode = (p) => {
    if (!p) return null;
    if (typeof p === 'string') return p;
    if (typeof p === 'object') {
      return p.codename || p.action_code || p.code || p.name || null;
    }
    return null;
  };

  if (Array.isArray(userInfo?.permission_codenames) && userInfo.permission_codenames.length) {
    const codes = userInfo.permission_codenames.map(normalizeCode).filter(Boolean);
    return new Set(codes);
  }
  const own = (userInfo?.user_permissions || userInfo?.permissions || []).map((p) => normalizeCode(p));
  const fromGroups = (userInfo?.groups || [])
    .flatMap((g) => g?.permissions || [])
    .map((p) => normalizeCode(p));
  return new Set([...own, ...fromGroups].filter(Boolean));
}

function patternRank(pattern) {
  const parts = normalizePath(pattern).split('/').filter(Boolean);
  let rank = parts.length * 1000;
  parts.forEach((seg) => {
    rank += seg.startsWith(':') ? 1 : 50;
  });
  rank += normalizePath(pattern).length;
  return rank;
}

let cachedPatterns = null;
let cachedMap = null;

function buildPatternCodenameMap() {
  const codenamesForPattern = {}; // pattern string -> Set
  const explicitPaths = new Set();

  const addCode = (pattern, code) => {
    const p = normalizePath(pattern);
    if (!p || !code) return;
    if (!codenamesForPattern[p]) codenamesForPattern[p] = new Set();
    codenamesForPattern[p].add(code);
  };

  Object.keys(Actions).forEach((actionKey) => {
    const action = Actions[actionKey];
    screenTypes.forEach((st) => {
      const sa = action[st];
      if (!sa?.url || !sa?.permission_needed || !sa?.action_code) return;
      const p = normalizePath(sa.url);
      explicitPaths.add(p);
      addCode(p, sa.action_code);
    });
  });

  Object.keys(Actions).forEach((actionKey) => {
    const action = Actions[actionKey];
    screenTypes.forEach((st) => {
      const sa = action[st];
      if (!sa?.associated_urls?.length || !sa?.permission_needed || !sa?.action_code) return;
      sa.associated_urls.forEach((assoc) => {
        const p = normalizePath(assoc);
        if (explicitPaths.has(p)) return;
        addCode(p, sa.action_code);
      });
    });
  });

  const userAddPat = normalizePath('/user/add');
  if (codenamesForPattern[userAddPat]) {
    codenamesForPattern[userAddPat].add('visible_user_change');
  }

  const visitsPat = normalizePath('/visits');
  if (codenamesForPattern[visitsPat]) {
    codenamesForPattern[visitsPat].add('visible_company_visit_change');
    codenamesForPattern[visitsPat].add('visible_company_visit_view');
  }

  const companyJsonCfgPat = normalizePath('/company-json-config');
  if (codenamesForPattern[companyJsonCfgPat]) {
    codenamesForPattern[companyJsonCfgPat].add('visible_company_json_config_change');
    codenamesForPattern[companyJsonCfgPat].add('visible_company_json_config_view');
  }

  const EXTRA_PATHS_MULTI = {
    '/agreement/edit': ['visible_agreement_view', 'visible_agreement_add'],
    '/billing/issue-invoice/tax-invoice': [
      'visible_company_issue_invoice_view',
      'visible_company_issue_invoice_change',
      'visible_company_issue_invoice_add',
    ],
    // Form is used to view existing orders and create new ones
    '/billing/orders-v2/form': [
      'visible_orders_v2_view',
      'visible_orders_v2_add',
      'visible_orders_v2_change',
    ],
    '/billing/orders-v2/id-cards': [
      'visible_orders_v2_view',
      'visible_id_card_orders_view',
    ],
    '/ticket/detail': ['visible_ticket_list_view'],
    '/basicdetails/feature-adds/edit/:id': [
      'visible_feature_adds_view',
      'visible_feature_adds_change',
    ],
    '/permissions': ['visible_permissions_menu_view'],
  };

  Object.entries(EXTRA_PATHS_MULTI).forEach(([path, codes]) => {
    codes.forEach((c) => addCode(path, c));
  });

  addCode('/company/details/:companyId', 'visible_company_list_view');
  addCode('/ticket/list/:companyId/detail', 'visible_ticket_list_view');

  cachedPatterns = Object.keys(codenamesForPattern).sort(
    (a, b) => patternRank(b) - patternRank(a) || b.length - a.length
  );
  cachedMap = codenamesForPattern;
}

export function pathRequiresSuperuserOnly(pathname) {
  const norm = normalizePath(pathname);
  const stypes = ['view', 'create', 'update', 'delete'];

  for (const actionKey of Object.keys(Actions)) {
    const action = Actions[actionKey];
    for (const screenType of stypes) {
      const sa = action[screenType];
      if (!sa?.is_superuser_action) continue;

      if (sa.url) {
        if (sa.url.includes(':')) {
          if (matchPath({ path: sa.url, end: true }, pathname)) return true;
        } else if (norm === normalizePath(sa.url)) {
          return true;
        }
      }

      const assoc = sa.associated_urls;
      if (!Array.isArray(assoc)) continue;
      for (const u of assoc) {
        const pNorm = normalizePath(u);
        if (!u.includes(':')) {
          if (norm === pNorm) return true;
        } else if (matchPath({ path: u, end: true }, pathname)) {
          return true;
        }
      }
    }
  }
  return false;
}

/**
 * Codenames sufficient to access pathname (logical OR — user needs at least one).
 */
export function getRequiredCodenamesForPath(pathname) {
  if (!pathname) return [];
  if (!cachedMap) buildPatternCodenameMap();

  for (let i = 0; i < cachedPatterns.length; i += 1) {
    const pattern = cachedPatterns[i];
    const m = matchPath({ path: pattern, end: true }, pathname);
    if (m) {
      const set = cachedMap[pattern];
      return set ? [...set] : [];
    }
  }
  return [];
}

export function computeCanAccessRoute(pathname) {
  const path = pathname || '';

  const { userInfo, isServerMenu } = parseUserFromStorage();
  const isSuperuser = computeIsSuperuser(userInfo);
  if (isSuperuser) return true;

  const userCodes = computeUserPermissionCodes(userInfo);

  if (isServerMenu && userCodes.size === 0) {
    return true;
  }

  if (pathRequiresSuperuserOnly(path)) {
    return false;
  }

  const needed = getRequiredCodenamesForPath(path);
  if (!needed.length) return true;

  return needed.some((c) => userCodes.has(c));
}
